// TYPES
import { useState } from 'react';
import { useSelector } from 'react-redux';
import type { IPermission } from '@timeedit/types/lib/types';
import { Icon } from '@timeedit/ui-components';
import {
  Button,
  Popconfirm,
  Select,
  Space,
  Table,
} from '@timeedit/ui-components/antd';
// i18N
import intl from '../../../i18n/intl';
import { scopesSelector } from '../../../slices/permission.slice';

const language: any = intl.messages;

type Props = {
  userScopeValues: string[];
  onChange: (scopes: string[]) => void;
  disabled?: boolean;
};

const ScopesTable = ({
  userScopeValues,
  onChange,
  disabled = false,
}: Props) => {
  const scopes: IPermission[] = useSelector(scopesSelector);

  const [newValue, setNewValue] = useState<string | undefined>(undefined);

  // EVENT HANDLERS
  const onAdd = () => {
    if (!newValue) return;
    onChange([...userScopeValues, newValue]);
    setNewValue(undefined);
  };

  const onDelete = (value: string) => {
    const idx = userScopeValues.indexOf(value);
    if (idx === -1) return;
    onChange([
      ...userScopeValues.slice(0, idx),
      ...userScopeValues.slice(idx + 1),
    ]);
  };

  // Columns
  return (
    <div>
      <Table
        size="small"
        dataSource={scopes.filter(
          (scope) => userScopeValues.indexOf(scope.value) > -1
        )}
        columns={[
          {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            width: 260,
          },
          {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
            width: 260,
          },
          {
            title: 'Value',
            dataIndex: 'value',
            key: 'value',
          },
          {
            title: language.action,
            key: 'action',
            dataIndex: undefined,
            render: (_, scope) => (
              <Space size="middle">
                <Popconfirm
                  title={language.sure_to_delete}
                  onConfirm={() => onDelete(scope.value)}
                  okText={language.yes}
                  disabled={disabled}
                  cancelText={language.no}
                >
                  <Button
                    size="small"
                    danger
                    ghost
                    type="default"
                    disabled={disabled}
                    className="types-list-action-button-delete"
                    icon={<Icon id="DeleteOutlined" alt="Delete" />}
                  />
                </Popconfirm>
              </Space>
            ),
          },
        ]}
        rowKey="value"
        style={{ marginBottom: '16px', marginTop: '16px' }}
        pagination={false}
        footer={() => (
          <Space direction="horizontal">
            <Select
              disabled={disabled}
              placeholder="Select a scope to add..."
              value={newValue}
              onChange={(val) => setNewValue(val)}
              size="small"
              style={{ width: '200px' }}
            >
              {scopes
                .filter((scope) => !userScopeValues.includes(scope.value))
                .map((scope) => (
                  <Select.Option value={scope.value} key={scope.value}>
                    {scope.name}
                  </Select.Option>
                ))}
            </Select>
            <Button
              type="default"
              size="small"
              icon={<Icon id="PlusOutlined" alt="Plus" />}
              onClick={onAdd}
              disabled={!newValue || disabled}
            >
              Add scope
            </Button>
          </Space>
        )}
      />
    </div>
  );
};

export default ScopesTable;
